import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { fetchResourceActions } from '~src/resourceActions/actions/ResourceActions';
import { canPerformAnyAction, getActionsByResourceType, isFetchingResourceActions as selectIsFetchingResourceActions } from '~src/resourceActions/selectors/ResourceActionsSelectors';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { GroupSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { GroupSpaceProvider, GroupSpaceProviderProps } from './GroupSpaceProvider';

export interface SpaceRouteParams {
  readonly id: string;
}

export interface GroupRouteComponentProps extends RouteComponentProps<SpaceRouteParams> {}

export interface GroupSpaceRouterOwnProps {
  readonly siteName: string;}

export interface GroupSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
  readonly canSeeUsers: boolean;
  readonly isFetchingResourceActions: boolean;
}

export interface GroupSpaceRouterDispatchProps {
  readonly fetchResourceActions: () => void;
}

export type GroupSpaceRouterProps = GroupSpaceRouterOwnProps & GroupSpaceRouterStateProps & GroupSpaceRouterDispatchProps;

export class GroupSpaceRouterComponent extends React.PureComponent<GroupSpaceRouterProps> {

  public componentDidMount() {
    if (getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
      this.props.fetchResourceActions();
    }
  }

  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.groupUsersPath(':id')} render={this.renderUsersSubspace} />
        <Route exact path={this.props.urls.groupDetailsPath(':id')} render={this.provider('details')} />
        <Route exact path={this.props.urls.groupPath(':id')} render={this.renderGroupRootRedirect} />
        <Route render={this.error} />
      </Switch>
    );
  }

  private renderUsersSubspace = (props: GroupRouteComponentProps): JSX.Element | null => {
    if (!getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
      return this.provider('users')(props);
    }
    if (this.props.isFetchingResourceActions) {
      return null;
    }
    if (this.props.canSeeUsers) {
      return this.provider('users')(props);
    }
    return <Redirect to={this.props.urls.groupDetailsPath(props.match.params.id)} />;
  };

  private renderGroupRootRedirect = ({ match }: GroupRouteComponentProps): JSX.Element | null => {
    if (!getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
      return <Redirect to={this.props.urls.groupUsersPath(match.params.id)} />;
    }
    if (this.props.isFetchingResourceActions) {
      return null;
    }
    if (this.props.canSeeUsers) {
      return <Redirect to={this.props.urls.groupUsersPath(match.params.id)} />;
    }
    return <Redirect to={this.props.urls.groupDetailsPath(match.params.id)} />;
  };

  private provider = (subspaceName: GroupSubspaceName): (props: RouteComponentProps) => JSX.Element => {
    return (props: GroupRouteComponentProps): JSX.Element => {
      const groupSpaceProps: GroupSpaceProviderProps = {
        urls: this.props.urls,
        siteName: this.props.siteName,
        subspaceName,
        routeLocationSearch: props.location.search,
        routeLocationPath: null,
        groupId: props.match.params.id
      };

      return <GroupSpaceProvider {...groupSpaceProps}/>;
    };
  };

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  };
}

const mapStateToProps: MapStateToProps<GroupSpaceRouterStateProps> = (appState: AppState) => {
  const isFetching = selectIsFetchingResourceActions(appState);
  const actionsByType = getActionsByResourceType(appState);
  const isAdminProfilesEnabled = getSessionManagementServiceInstance().isAdminProfilesEnabled();
  // Treat as loading if: actively fetching, OR if isAdminProfilesEnabled and we have
  // never fetched yet (empty map, no error). This prevents a false-negative access denial
  // on the very first render before componentDidMount has dispatched the fetch.
  const neverFetched = isAdminProfilesEnabled && !isFetching && actionsByType.size === 0;
  return {
    urls: siteUrlsSelector(appState),
    routerReactKey: appState.routerSpaceReactKey,
    canSeeUsers: isAdminProfilesEnabled
      ? canPerformAnyAction(appState, 'User', ['SEE_SITE_USERS'])
      : true,
    isFetchingResourceActions: isFetching || neverFetched,
  };
};

const mapDispatchToProps: MapDispatchToProps<GroupSpaceRouterDispatchProps> = (dispatch) =>
  bindActionCreators({ fetchResourceActions }, dispatch);

export const GroupSpaceRouter: React.ComponentClass<GroupSpaceRouterOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(GroupSpaceRouterComponent);
